import React, { useEffect, useState } from "react";
import Card from "components/card";

import { tcpTableApi, tcpTableXcelApi } from "api/dashboard";
import { Combobox } from "@headlessui/react";
import { getAllProductsNameApi } from "api/filtersData";
import { Spinner } from "@material-tailwind/react";
import API_URL from "config";
import BarChart from "components/charts/BarChart";

const TCPTable = () => {
  const [comboBoxData, setComboBoxData] = useState([]);
  const [selectedComboBoxData, setSelectedComboBoxData] = useState([]);
  const [query, setQuery] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingXcel, setIsLoadingXcel] = useState(false);

  const [tableData, setTableData] = useState([]);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const filteredComboBoxData =
    query === ""
      ? comboBoxData
      : comboBoxData.filter((value) =>
          value
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const getInitialData = () => {
    try {
      setIsLoading(true);

      if (fromDate && toDate) {
        tcpTableApi(
          selectedComboBoxData,
          fromDate,
          toDate
        )
        .then((tcpTableRes) => {
          setTableData(tcpTableRes);
        })
        .catch (err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false);
        })
      } else {
        tcpTableApi(selectedComboBoxData, null, null)
        .then((tcpTableRes) => {
          setTableData(tcpTableRes);
        })
        .catch (err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false);
        })
      }

      getAllProductsNameApi()
      .then(AllProductsName => {
        setComboBoxData(AllProductsName);
        setComboBoxData(["Tout", ...AllProductsName]);
      })
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterData = () => {
    try {
      getInitialData();
    }
    catch (err) {
      
    }
  }

  const handleRefreshFilter = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedComboBoxData([]);
    setQuery("");
    getInitialData();
  }

  const handleDownloadXcel = async () => {
    try {
      setIsLoadingXcel(true);
      if (fromDate && toDate) {
        const productXcelTableRes = await tcpTableXcelApi(
          selectedComboBoxData,
          fromDate,
          toDate
        );
        window.open(`${API_URL}/${productXcelTableRes}`, "_blank");
      } else {
        const productXcelTableRes = await tcpTableXcelApi(
          selectedComboBoxData,
          null,
          null
        );
        window.open(`${API_URL}/${productXcelTableRes}`, "_blank");
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoadingXcel(false);
    }
  };

  const handleProductSelection = (product) => {
    if (product === "Tout") {
      setSelectedComboBoxData([]);
    } else {
      setSelectedComboBoxData(prev => {
        if (prev.includes(product)) {
          return prev.filter(p => p !== product);
        } else {
          return [...prev, product];
        }
      });
    }
    // Clear the search query after selection
    setQuery("");
  };

  const getSelectedProductsDisplay = () => {
    if (selectedComboBoxData.length === 0) return "Sélectionner des produits";
    if (selectedComboBoxData.length === 1) return selectedComboBoxData[0];
    return `${selectedComboBoxData.length} produits sélectionnés`;
  };

  // Format number with space as thousand separators
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Build aggregates per TCP for charts
  const getTcpAggregates = () => {
    if (!tableData?.data) return [];
    return Object.entries(tableData.data).map(([tcp, arr]) => {
      const last = arr[arr.length - 1] || {};
      const quantity = parseFloat(last?.sumOfQuantity) || 0;
      const amount = parseFloat(last?.sumOfAmount) || 0;
      return { tcp, quantity, amount };
    });
  };

  const getQuantityChartData = () => {
    const aggregates = getTcpAggregates();
    const sorted = aggregates.sort((a, b) => b.quantity - a.quantity);
    const categories = sorted.map((x) => x.tcp);
    const data = sorted.map((x) => x.quantity);
    return {
      series: [{ name: 'Quantité', data }],
      categories,
      height: Math.max(320, categories.length * 28),
    };
  };

  const getAmountChartData = () => {
    const aggregates = getTcpAggregates();
    const sorted = aggregates.sort((a, b) => b.amount - a.amount);
    const categories = sorted.map((x) => x.tcp);
    const amountData = sorted.map((x) => x.amount);
    const quantityData = sorted.map((x) => x.quantity);
    return {
      series: [
        { 
          name: 'Montant HT', 
          data: amountData.map((amount, index) => ({
            x: categories[index],
            y: amount,
            quantity: quantityData[index]
          }))
        }
      ],
      categories,
      height: Math.max(320, categories.length * 28),
    };
  };

  const getCombinedBarOptions = (categories) => {
    return {
      chart: {
        type: 'bar',
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '60%',
          borderRadius: 4,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: { fontSize: '12px' },
        },
        title: { text: 'Montant HT (DH)' },
        tickAmount: 6,
      },
      yaxis: {
        labels: {
          style: { fontSize: '12px' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${formatNumber(val)} DH`,
        offsetX: 8,
        style: { 
          fontSize: '13px', 
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        },
      },
      tooltip: {
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
          const seriesData = (w && w.globals && w.globals.initialSeries && w.globals.initialSeries[seriesIndex] && w.globals.initialSeries[seriesIndex].data) ? w.globals.initialSeries[seriesIndex].data : [];
          const total = seriesData.reduce((sum, d) => sum + (typeof d === 'object' ? (d.y || 0) : (d || 0)), 0);
          const pct = total ? (data.y / total) * 100 : 0;
          const amount = formatNumber(data.y);
          const quantity = formatNumber(data.quantity);
          return `
            <div style="padding: 12px; background: white; border: 1px solid #ccc; border-radius: 6px; box-shadow: 0 3px 8px rgba(0,0,0,0.15); min-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${data.x}</div>
              <div style="color: #16a34a; font-weight: bold; font-size: 16px; margin-bottom: 4px;">Montant HT: ${amount} DH (${pct.toFixed(1)}%)</div>
              <div style="color: #2563eb; font-weight: bold; font-size: 16px;">Quantité: ${quantity}</div>
            </div>
          `;
        }
      },
      colors: ['#16a34a'],
      grid: { strokeDashArray: 4 },
      legend: { show: false },
    };
  };

  const getHorizontalBarOptions = (categories, color = '#236571', isAmount = false) => {
    return {
      chart: {
        type: 'bar',
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '60%',
          borderRadius: 4,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: { fontSize: '12px' },
        },
        title: { text: isAmount ? 'Montant HT (DH)' : 'Quantité' },
        tickAmount: 6,
      },
      yaxis: {
        labels: {
          style: { fontSize: '12px' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => isAmount ? `${formatNumber(val)} DH` : `${formatNumber(val)}`,
        offsetX: 8,
        style: { 
          fontSize: '13px', 
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        },
      },
      tooltip: {
        y: {
          formatter: (val) => (isAmount ? `${formatNumber(val)} DH` : `${formatNumber(val)}`),
        },
      },
      colors: [color],
      grid: { strokeDashArray: 4 },
      legend: { show: false },
    };
  };

  useEffect(() => {
    getInitialData();
  }, []); // selectedComboBoxData, fromDate, toDate

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="h-12 w-12" />
        </div>
      ) : (
        <Card extra={"w-full pb-10 p-4 h-full"}>
          <header className="relative flex items-center justify-between">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              TABLE TCP
            </div>
            {isLoadingXcel ? (
              <div className="flex items-center justify-center">
                <Spinner className="h-12 w-12" />
              </div>
            ) : (
              <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleDownloadXcel}
              >
                Télécharger excel
              </button>
            )}
          </header>

          <div className="mt-5 grid grid-cols-2 gap-5">
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par date
              </small>
              <div>
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </div>
            </div>
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par produit
              </small>
              <div>
                <Combobox value="" onChange={handleProductSelection}>
                  <Combobox.Input
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={() => ""}
                    className="relative mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
                    placeholder="Sélectionner des produits"
                  />
                  <Combobox.Options className="absolute right-0 h-32 w-1/2 overflow-y-scroll bg-white z-10">
                    {filteredComboBoxData?.map((value, index) => (
                      <Combobox.Option
                        key={index}
                        value={value}
                        className="cursor-pointer rounded-xl p-2 hover:bg-gray-100"
                      >
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedComboBoxData.includes(value)}
                            readOnly
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span>{value}</span>
                        </div>
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </Combobox>
                {/* Display selected products below the input */}
                {selectedComboBoxData.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {selectedComboBoxData.length === 1 
                      ? selectedComboBoxData[0] 
                      : `${selectedComboBoxData.length} produits sélectionnés`
                    }
                  </div>
                )}
              </div>
            </div>

            <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleFilterData}
              >
                Filtrer
              </button>

            <button
                href=""
                className="linear rounded-[10px] bg-blue-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-blue-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleRefreshFilter}
              >
                Actualiser
              </button>

          </div>

          {/* Charts: Combined Montant HT and Quantité by TCP */}
          {tableData?.data && Object.keys(tableData.data).length > 0 && (
            <div className="mt-6">
              <Card extra={"w-full p-4"}>
                <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">TCP - Montant HT (trié)</div>
                {(() => {
                  const { series, categories, height } = getAmountChartData();
                  if (!series?.[0]?.data?.length) {
                    return <div className="text-sm text-gray-500">Aucune donnée à afficher.</div>;
                  }
                  return (
                    <BarChart
                      chartData={series}
                      chartOptions={getCombinedBarOptions(categories)}
                      width="100%"
                      height={height}
                    />
                  );
                })()}
              </Card>
            </div>
          )}

          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">TCP</p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Quantité
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Montant HT
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData?.data ? (
                  Object.entries(tableData?.data).map(([key, value]) => {
                    const Quantity = tableData?.data[key][tableData?.data[key].length-1].sumOfQuantity
                    const Price = tableData?.data[key][tableData?.data[key].length-1].sumOfAmount

                    return (
                      <tr>
                        <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                          <p className="text-sm font-bold text-navy-700 dark:text-white">
                            {key}
                          </p>
                        </td>
                        <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                          <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                            {formatNumber(Quantity.toFixed(2))}
                          </p>
                        </td>
                        <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                          <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                            {formatNumber(Price.toFixed(2))}
                          </p>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <></>
                )}

                <tr className="bg-gray-100 dark:bg-brand-400">
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfQuantity?.toFixed(2))}
                    </p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfAmount?.toFixed(2))}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
};

export default TCPTable;